// src/components/TestPublicProfileScreen.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { FiArrowRight, FiExternalLink, FiCopy, FiCheck } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const TestPublicProfileScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [providerId, setProviderId] = useState(user?.id || '');
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}${window.location.pathname}#/provider-public/${providerId}`;

  const handleOpenProfile = () => {
    if (!providerId.trim()) {
      toast.error('يرجى إدخال معرف المزود');
      return;
    }
    
    navigate(`/provider-public/${providerId}`);
  };

  const handleOpenInNewTab = () => {
    if (!providerId.trim()) {
      toast.error('يرجى إدخال معرف المزود');
      return;
    }
    
    window.open(`#/provider-public/${providerId}`, '_blank');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('تم نسخ الرابط');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('فشل نسخ الرابط');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md p-4 text-white shadow-lg flex items-center justify-between sticky top-0 z-50">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/20 transition-colors"
        >
          <FiArrowRight className="text-xl" />
        </button>
        <h1 className="text-lg font-bold">اختبار الصفحة العامة</h1>
        <div className="w-10"></div>
      </div>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Main Card */}
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full mb-4">
              <FiExternalLink className="text-white text-3xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">اختبار الملف الشخصي العام</h2>
            <p className="text-gray-600">أدخل معرف المزود لمشاهدة الصفحة العامة</p>
          </div>

          {/* User Info */}
          {user && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6 border border-blue-200">
              <p className="text-sm text-gray-600 mb-2">معرفك الحالي:</p>
              <div className="flex items-center justify-between">
                <code className="text-xs bg-white px-3 py-2 rounded-lg text-gray-800 font-mono flex-1 mr-2 overflow-x-auto">
                  {user.id}
                </code>
                <button
                  onClick={() => setProviderId(user.id)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  استخدام
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">
              معرف المزود (Provider ID)
            </label>
            <input
              type="text"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              placeholder="أدخل معرف المزود هنا..."
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-gray-800"
              dir="ltr"
            />
          </div>

          {/* Generated URL */}
          {providerId && (
            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-3">
                الرابط المولد
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-100 px-4 py-3 rounded-xl overflow-x-auto">
                  <code className="text-xs text-gray-800 font-mono" dir="ltr">
                    {publicUrl}
                  </code>
                </div>
                <button
                  onClick={copyToClipboard}
                  className="bg-gray-800 text-white p-3 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  {copied ? <FiCheck className="text-lg" /> : <FiCopy className="text-lg" />}
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleOpenProfile}
              disabled={!providerId.trim()}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              🚀 فتح الصفحة العامة
            </button>

            <button
              onClick={handleOpenInNewTab}
              disabled={!providerId.trim()}
              className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-4 rounded-xl font-bold text-lg hover:from-green-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              📱 فتح في تبويب جديد
            </button>
          </div>
        </div>

        {/* Info Boxes */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <h3 className="text-xl font-bold text-gray-900 mb-4">📌 معلومات مهمة</h3>
          <div className="space-y-4">
            <div className="bg-blue-50 border-r-4 border-blue-500 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">كيف أحصل على معرف المزود؟</h4>
              <p className="text-blue-800 text-sm">
                إذا كنت مسجلاً كمزود، معرفك موجود أعلاه. يمكنك أيضاً الحصول عليه من قاعدة البيانات أو من رابط بطاقتك المهنية.
              </p>
            </div>

            <div className="bg-green-50 border-r-4 border-green-500 p-4 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">💡 نصيحة</h4>
              <p className="text-green-800 text-sm">
                الصفحة العامة متاحة للجميع (مع أو بدون تسجيل دخول). يمكنك مشاركة الرابط مع أي شخص!
              </p>
            </div>

            <div className="bg-yellow-50 border-r-4 border-yellow-500 p-4 rounded-lg">
              <h4 className="font-semibold text-yellow-900 mb-2">⚠️ ملاحظة</h4>
              <p className="text-yellow-800 text-sm">
                تأكد من أن المزود موجود في قاعدة البيانات ولديه بيانات كاملة لعرض الصفحة بشكل صحيح.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Test Buttons */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <h3 className="text-xl font-bold text-gray-900 mb-4">⚡ اختبار سريع</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => {
                const testId = user?.id || 'test-provider-123';
                setProviderId(testId);
                toast.success('تم تعيين معرف اختبار');
              }}
              className="bg-indigo-600 text-white py-3 rounded-xl font-medium hover:bg-indigo-700 transition-colors"
            >
              استخدام معرفي
            </button>
            
            <button
              onClick={() => {
                setProviderId('');
                toast.info('تم مسح المعرف');
              }}
              className="bg-gray-600 text-white py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
            >
              مسح المعرف
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPublicProfileScreen;